import type { ReactNode } from "react";
import { Pressable, StyleSheet, View } from "react-native";

import { AppText } from "./AppText";
import {
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";

export function ListRow({
  title,
  description,
  meta,
  trailing,
  onPress,
  accessibilityLabel,
}: {
  title: ReactNode;
  description?: ReactNode;
  meta?: ReactNode;
  trailing?: ReactNode;
  onPress?: () => void;
  accessibilityLabel?: string;
}) {
  const stylesheet = useThemedStyles(makeStyles);
  const body = (
    <>
      <View style={stylesheet.main}>
        {typeof title === "string" ? (
          <AppText weight="semibold">{title}</AppText>
        ) : (
          title
        )}
        {description ? (
          typeof description === "string" ? (
            <AppText size="sm" tone="muted">
              {description}
            </AppText>
          ) : (
            description
          )
        ) : null}
        {meta ? <View style={stylesheet.meta}>{meta}</View> : null}
      </View>
      {trailing ? <View style={stylesheet.trailing}>{trailing}</View> : null}
    </>
  );

  if (onPress) {
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={
          accessibilityLabel ?? (typeof title === "string" ? title : undefined)
        }
        onPress={onPress}
        style={({ hovered, pressed }) => [
          stylesheet.row,
          hovered || pressed ? stylesheet.rowHover : null,
        ]}
      >
        {body}
      </Pressable>
    );
  }

  return <View style={stylesheet.row}>{body}</View>;
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    row: {
      minHeight: 60,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.m,
      paddingVertical: spacing.m,
      paddingHorizontal: spacing.l,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.m,
      backgroundColor: colors.elevated,
    },
    rowHover: {
      backgroundColor: colors.surface,
      borderColor: colors.borderStrong,
    },
    main: { flex: 1, minWidth: 0, gap: spacing.xs },
    meta: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.s,
      marginTop: spacing.xs,
    },
    trailing: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.s,
    },
  });
